<template>
  <HLModal
    :show="show"
    :show-header="true"
    :show-footer="hasFooter"
    :show-close="true"
    :show-header-icon="false"
    :mask-closable="maskClosable"
    :close-on-esc="closeOnEsc"
    :width="width"
    :class="modalClass"
    @update:show="(value) => !value && $emit('close')"
  >
    <template #header>
      <div class="flex items-center gap-3 px-3 pt-2 pb-3">
        <div :class="['flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg', iconBgClass]">
          <HLIcon
            :size="20"
            :class="iconColorClass"
          >
            <slot name="icon" />
          </HLIcon>
        </div>
        <div>
          <HLText
            size="2xl"
            weight="semibold"
            html-tag="span"
          >
            {{ title }}
          </HLText>
          <HLText
            v-if="subtitle"
            size="sm"
            color="muted"
            class="block"
          >
            {{ subtitle }}
          </HLText>
        </div>
      </div>
    </template>

    <template #default>
      <slot />
    </template>

    <template
      v-if="hasFooter"
      #footer
    >
      <div class="modal-footer">
        <slot name="footer" />
      </div>
    </template>
  </HLModal>
</template>

<script setup>
import { computed, useSlots } from 'vue'
import { HLModal, HLText, HLIcon } from '@platform-ui/highrise'

const props = defineProps({
  show: { type: Boolean, required: true },
  title: { type: String, required: true },
  subtitle: { type: String, default: '' },
  /** primary | red | green | orange | blue */
  iconColor: { type: String, default: 'primary' },
  width: { type: [Number, String], default: 600 },
  maskClosable: { type: Boolean, default: true },
  closeOnEsc: { type: Boolean, default: true },
  modalClass: { type: String, default: '' }
})

defineEmits(['close'])

const slots = useSlots()
const hasFooter = computed(() => !!slots.footer)

const BG_MAP = {
  primary: 'bg-primary-50 dark:bg-primary-900/30',
  red:     'bg-red-50 dark:bg-red-900/20',
  green:   'bg-green-50 dark:bg-green-900/20',
  orange:  'bg-orange-50 dark:bg-orange-900/20',
  blue:    'bg-blue-50 dark:bg-blue-900/20',
}

const COLOR_MAP = {
  primary: 'text-primary-600 dark:text-primary-400',
  red:     'text-red-600 dark:text-red-400',
  green:   'text-green-600 dark:text-green-400',
  orange:  'text-orange-600 dark:text-orange-400',
  blue:    'text-blue-600 dark:text-blue-400',
}

const iconBgClass    = computed(() => BG_MAP[props.iconColor]    ?? BG_MAP.primary)
const iconColorClass = computed(() => COLOR_MAP[props.iconColor] ?? COLOR_MAP.primary)
</script>
